import { AxiosError } from 'axios'

import { client } from './client'
import * as ENDPOINTS from './endpoints'
import { ProfileType } from './profile'

export interface ConnectionRequestType extends JSON {
  userIds: string[]
  name: string
  connectionType?: 'friend' | 'group'
}

export interface ConnectionRequestResponseType {
  message?: string
  detail?: string
  connection?: ConnectionDetailType
  mutualConnection?: boolean
}

export interface ResponseToConnectionType extends JSON {
  response: 'accepted' | 'rejected'
}

export interface ConnectionDetailType extends JSON {
  id: string
  name: string
  createdAt: Date
  conversationSid: string
  connectionType: 'friend' | 'group'
  members: ConnectionMemeber[]
}

interface ConnectionMemeber extends JSON {
  id: string
  response: 'accepted' | 'rejected' | 'pending'
  responseAt: Date
  joinedAs: 'sender' | 'receiver'
  user: string
  userName: string
  userEmail: string
  connectionName: string
  userAvatar: string
  userPostcode?: string // Member's postcode from backend API
}

export interface RequestConnectionBySid extends JSON {
  connectionSid: string
}

export interface UpdateGroupNamePayload extends JSON {
  name: string
}

export interface CalendarEventCreate extends JSON {
  title: string
  description: string
  eventDate: string
  startTime: string
  endTime: string
  timezone: string
  location: string
  locationPoint?: {
    type: 'Point'
    coordinates: [number, number]
  }
  invitedFriends?: string[]
}

export interface RecurrenceRule {
  id?: number
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
  interval: number
  byWeekday?: number[] // 0=Monday, 6=Sunday
  byMonthday?: number // Day of month for MONTHLY frequency
  byMonth?: number
  count?: number
  until?: string // date string
  // Note: timezone, timeStart, timeEnd have been moved to Event level
  recurrenceDescription?: string
  startDate?: string // Start date for recurring pattern (YYYY-MM-DD format)
}

export interface RecurringEventCreate extends CalendarEventCreate {
  recurrenceRule?: RecurrenceRule
}

// Removed unused occurrence update/delete payload types

export interface AvailableFriend {
  id: string
  name: string
  avatar: string | null
}

export interface CalendarEventResponse {
  message?: string
  detail?: string
  eventId?: string
}

// Payload for inviting new members to a group
export interface InviteMembersPayload extends JSON {
  userIds: string[]
}

export interface ConnectionStats {
  friendsCount: number
  groupsCount: number
  pendingFriendRequestsCount: number
  pendingGroupRequestsCount: number
  pendingEventsCount: number
}

export const getConnectionStats = async (): Promise<ConnectionStats> => {
  const response = await client.get<ConnectionStats>(ENDPOINTS.GET_CONNECTION_STATS)
  return response.data
}

export const sendConnectionRequest = async (
  data: ConnectionRequestType
): Promise<ConnectionRequestResponseType> => {
  try {
    const response = await client.post<ConnectionRequestResponseType, ConnectionRequestType>(
      ENDPOINTS.SEND_REQUEST,
      data
    )
    // Return the full response data to preserve mutualConnection flag
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

type ConnectionResponseData = ConnectionMemeber | { message: string }

export const responseToConnectionRequest = async (
  data: ResponseToConnectionType,
  connectionId: string
): Promise<ConnectionRequestResponseType> => {
  try {
    const response = await client.put<ConnectionResponseData, ResponseToConnectionType>(
      `${ENDPOINTS.RESPOND_REQUEST}${connectionId}/`,
      data
    )
    if ('message' in response.data) {
      return { message: response.data.message }
    } else {
      // For acceptance, return a success indicator
      return { message: 'accepted' }
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getOneConnectionDetail = async (
  connectionId: string
): Promise<ConnectionDetailType | ConnectionRequestResponseType> => {
  try {
    const response = await client.get<ConnectionDetailType>(
      `${ENDPOINTS.GET_ONE_CONNECTION}${connectionId}/`
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getUserConnectionsByStatus = async (
  joinedAS: string,
  status: string
): Promise<ConnectionDetailType[] | ConnectionRequestResponseType> => {
  try {
    const response = await client.get<ConnectionDetailType[]>(
      `${ENDPOINTS.GET_REQUEST_LIST}${status}/${joinedAS}/`
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getUserConnectionByJoinedType = async (
  joinedAs: string
): Promise<ConnectionDetailType[] | ConnectionRequestResponseType> => {
  try {
    const response = await client.get<ConnectionDetailType[]>(
      `${ENDPOINTS.GET_ALL_REQUESTS}${joinedAs}/`
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getUserAllConnections = async (): Promise<
  ConnectionDetailType[] | ConnectionRequestResponseType
> => {
  try {
    const response = await client.get<ConnectionDetailType[]>(ENDPOINTS.GET_CONNECTIONS)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getUserFriendsList = async (): Promise<
  ConnectionDetailType[] | ConnectionRequestResponseType
> => {
  try {
    const response = await client.get<ConnectionDetailType[]>(ENDPOINTS.GET_FRIENDS_LIST)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getUserGroupsList = async (): Promise<
  ConnectionDetailType[] | ConnectionRequestResponseType
> => {
  try {
    const response = await client.get<ConnectionDetailType[]>(ENDPOINTS.GET_GROUPS_LIST)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const cancelConnection = async (): Promise<ConnectionRequestResponseType> => {
  try {
    const response = await client.delete<ConnectionRequestResponseType>(ENDPOINTS.CANCEL_CONNECTION)
    return { message: response.data.message }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Connection can not found' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getPossibleConectionUserList = async (): Promise<
  ProfileType[] | ConnectionRequestResponseType
> => {
  try {
    const response = await client.get<ProfileType[]>(ENDPOINTS.GET_POSSIBLE_CONNECTIONS)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'user info not found' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getNonMatchingUserList = async (): Promise<
  ProfileType[] | ConnectionRequestResponseType
> => {
  try {
    const response = await client.get<ProfileType[]>(ENDPOINTS.GET_NOT_MATCH_CONNECTIONS)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'user info not found' }
      }
      if (error.response.status === 404) {
        return { message: 'Profile not found' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const getConnectionBySid = async (
  data: RequestConnectionBySid
): Promise<ConnectionDetailType | ConnectionRequestResponseType> => {
  try {
    const response = await client.get<ConnectionDetailType>(
      ENDPOINTS.GET_CONNECTION_BY_SID + data.connectionSid
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Connection not found' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

export const createCalendarEvent = async (
  data: CalendarEventCreate
): Promise<CalendarEventResponse> => {
  try {
    const response = await client.post<CalendarEventResponse, CalendarEventCreate>(
      ENDPOINTS.CREATE_CALENDAR_EVENT,
      data
    )
    return {
      message: response.data.message,
      eventId: response.data.eventId,
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        throw new Error('Input info invalid')
      }
    }
    throw new Error('Failed to create event')
  }
}

export const getAvailableFriends = async (): Promise<
  AvailableFriend[] | ConnectionRequestResponseType
> => {
  try {
    const response = await client.get<AvailableFriend[]>(ENDPOINTS.GET_AVAILABLE_FRIENDS)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        return { message: 'Input info invalid' }
      }
    }
    return { message: 'Fail to submit' }
  }
}

// -----------------------------------------------------------------------------
// Group invitation – invite new members to an existing group connection
// -----------------------------------------------------------------------------

export const inviteMembersToGroup = async (
  connectionId: string,
  userIds: string[]
): Promise<ConnectionRequestResponseType> => {
  try {
    const payload: InviteMembersPayload = { userIds } as InviteMembersPayload

    const response = await client.post<ConnectionRequestResponseType, InviteMembersPayload>(
      `${ENDPOINTS.INVITE_MEMBERS}${connectionId}/`,
      payload
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status } = error.response
      if (status === 403) {
        return { message: 'You are not authorised to invite members to this group' }
      }
      if (status === 404) {
        return { message: 'Connection not found' }
      }
      if (status === 400) {
        return { message: 'Invalid request' }
      }
    }
    return { message: 'Failed to invite members' }
  }
}

/**
 * Update the name of a group connection
 * Only the group creator can update the name
 */
export const updateGroupName = async (
  connectionId: string,
  name: string
): Promise<ConnectionRequestResponseType> => {
  try {
    const payload: UpdateGroupNamePayload = { name } as UpdateGroupNamePayload

    const response = await client.patch<ConnectionRequestResponseType, UpdateGroupNamePayload>(
      `${ENDPOINTS.UPDATE_GROUP_NAME}${connectionId}/`,
      payload
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status } = error.response
      if (status === 403) {
        return { message: 'Only the group creator can update the group name' }
      }
      if (status === 404) {
        return { message: 'Connection not found' }
      }
      if (status === 400) {
        const data = error.response.data as { detail?: string }
        return { message: data.detail ?? 'Invalid group name' }
      }
    }
    return { message: 'Failed to update group name' }
  }
}

/**
 * Remove a member from a group connection
 * Only the group creator can remove members
 */
export const removeMemberFromGroup = async (
  connectionId: string,
  userId: string
): Promise<ConnectionRequestResponseType> => {
  try {
    const response = await client.delete<ConnectionRequestResponseType>(
      `${ENDPOINTS.REMOVE_MEMBER}${connectionId}/${userId}/`
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status } = error.response
      if (status === 403) {
        return { message: 'Only the group creator can remove members' }
      }
      if (status === 404) {
        return { message: 'Member not found' }
      }
      if (status === 400) {
        const data = error.response.data as { detail?: string }
        return { message: data.detail ?? 'Invalid request' }
      }
    }
    return { message: 'Failed to remove member' }
  }
}

// -----------------------------------------------------------------------------
// Recurring events API functions
// -----------------------------------------------------------------------------

export const createRecurringEvent = async (
  data: RecurringEventCreate
): Promise<CalendarEventResponse> => {
  try {
    const response = await client.post<CalendarEventResponse, RecurringEventCreate>(
      ENDPOINTS.CREATE_RECURRING_EVENT,
      data
    )
    return {
      message: response.data.message,
      eventId: response.data.eventId,
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 400) {
        throw new Error('Input info invalid')
      }
    }
    throw new Error('Failed to create recurring event')
  }
}

// Removed unused occurrence and series update/delete functions
